import {
	getRoleKey,
	participantGrantKey,
} from "@elaso/shared/types/authorization";
import { ChevronDown, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useStore } from "zustand";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { coreStore, type Role } from "@/lib/stores/core-store";

export type RoleInfo = { key: string; label: string; type: string };

/**
 * The OpenFGA tenant the client's grant keys are scoped to. It must match the
 * server's `OPENFGA_TENANT_ID`, so a participant-grant key built here for the
 * "everyone" channel-permission row is byte-identical to the one the server
 * seeds and stores. Injected at build time by Vite.
 */
const OPENFGA_TENANT_ID = import.meta.env.VITE_OPENFGA_TENANT_ID as string;

const CATEGORY_ORDER = [
	"Workspace",
	"Cohort",
	"Team",
	"OI Table",
	"Teaching Team Cohort",
] as const;

/**
 * Classify a role by name pattern into one of the combobox category buckets.
 * Role names follow the seeded conventions:
 *   - `cohort--{1,2}`                           → Cohort
 *   - `team--{cohort}-{a..v}`                   → Team
 *   - `oi-table--{n}`                           → OI Table
 *   - `teaching-team-cohort--{1,2}`             → Teaching Team Cohort
 *   - everything else                           → Workspace
 */
function classifyRoleName(name: string): {
	type: string;
	label: string;
} {
	if (name === "everyone") return { type: "Workspace", label: "everyone" };
	if (name.startsWith("cohort--")) {
		return { type: "Cohort", label: `Cohort ${name.replace("cohort--", "")}` };
	}
	if (name.startsWith("team--")) {
		return { type: "Team", label: `Team ${name.replace("team--", "")}` };
	}
	if (name.startsWith("oi-table--")) {
		return {
			type: "OI Table",
			label: `OI Table ${name.replace("oi-table--", "")}`,
		};
	}
	if (name.startsWith("teaching-team-cohort--")) {
		return {
			type: "Teaching Team Cohort",
			label: `Teaching Team Cohort ${name.replace("teaching-team-cohort--", "")}`,
		};
	}
	return { type: "Workspace", label: name };
}

export function getAvailableRoles({
	workspaceId,
	roster,
	hideEveryone = false,
}: {
	workspaceId: string | null;
	roster: Map<string, Role>;
	hideEveryone?: boolean;
}): RoleInfo[] {
	if (!workspaceId) return [];
	const out: RoleInfo[] = [];

	if (!hideEveryone) {
		// "everyone" is offered as a role to pick, and it grants baseline access
		// through the workspace `participant` userset rather than a Role record
		// (TECH-2941,
		// docs/adr/11-workspace-participation-permission-structure.md). This row
		// grants and revokes that participant grant on the channel asset, and its
		// key matches the server's `participantGrantKey`.
		out.push({
			key: participantGrantKey(OPENFGA_TENANT_ID, workspaceId),
			label: "everyone",
			type: "Workspace",
		});
	}

	for (const role of roster.values()) {
		if (role.workspaceId !== workspaceId) continue;
		const { type, label } = classifyRoleName(role.name);
		out.push({
			key: getRoleKey({ kind: "named", workspaceId, id: role.id }),
			label,
			type,
		});
	}

	return out;
}

export function MultiSelectRoleCombobox({
	selectedRoles,
	onSelectionChange,
	workspaceId,
	placeholder = "Select roles...",
	hideEveryone = false,
	mode,
}: {
	selectedRoles: string[];
	onSelectionChange: (roleKeys: string[]) => void;
	placeholder?: string;
	hideEveryone?: boolean;
	workspaceId: string | null;
	mode?: "roles" | "assignments";
}) {
	const [open, setOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");

	const roster = useStore(coreStore, (state) => state.roles);
	const roles = useMemo(() => {
		const allRoles = getAvailableRoles({
			workspaceId,
			roster,
			hideEveryone,
		});
		if (!mode) return allRoles;
		if (mode === "roles") {
			return allRoles.filter((r) => r.type === "Workspace");
		}
		return allRoles.filter((r) => r.type !== "Workspace");
	}, [workspaceId, roster, hideEveryone, mode]);

	const rolesByCategory = useMemo(() => {
		const categories: Record<string, RoleInfo[]> = {};
		for (const role of roles) {
			if (!categories[role.type]) {
				categories[role.type] = [];
			}
			categories[role.type].push(role);
		}
		return categories;
	}, [roles]);

	const filteredRolesByCategory = useMemo(() => {
		if (!searchQuery.trim()) return rolesByCategory;

		const query = searchQuery.toLowerCase();
		const filtered: Record<string, RoleInfo[]> = {};

		for (const [category, categoryRoles] of Object.entries(rolesByCategory)) {
			const matchingRoles = categoryRoles.filter((role) =>
				role.label.toLowerCase().includes(query)
			);
			if (matchingRoles.length > 0) {
				filtered[category] = matchingRoles;
			}
		}

		return filtered;
	}, [rolesByCategory, searchQuery]);

	const handleToggle = (roleKey: string) => {
		if (selectedRoles.includes(roleKey)) {
			onSelectionChange(selectedRoles.filter((k) => k !== roleKey));
		} else {
			onSelectionChange([...selectedRoles, roleKey]);
		}
	};

	const selectedLabels = roles
		.filter((r) => selectedRoles.includes(r.key))
		.map((r) => r.label);

	return (
		<Popover modal onOpenChange={setOpen} open={open}>
			<PopoverTrigger asChild>
				<Button
					className="h-auto min-h-9 w-full justify-between px-3 py-1.5 font-normal"
					variant="outline"
				>
					<div className="flex flex-wrap gap-1">
						{selectedLabels.length === 0 && (
							<span className="text-muted-foreground">{placeholder}</span>
						)}
						{selectedLabels.length > 0 &&
							selectedLabels.map((label) => (
								<Badge className="text-xs" key={label} variant="secondary">
									{label}
								</Badge>
							))}
					</div>
					<ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-64 p-0">
				<div className="p-2">
					<div className="relative">
						<Search className="-translate-y-1/2 absolute top-1/2 left-2 size-3 text-muted-foreground" />
						<Input
							className="h-8 pl-7 text-sm"
							onChange={(e) => setSearchQuery(e.target.value)}
							placeholder="Search roles..."
							value={searchQuery}
						/>
					</div>
				</div>
				<ScrollArea className="h-72 overflow-hidden">
					<div className="p-1">
						{CATEGORY_ORDER.map((category) => {
							const categoryRoles = filteredRolesByCategory[category];
							if (!categoryRoles || categoryRoles.length === 0) return null;
							return (
								<div key={category}>
									<div className="px-2 py-1.5 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
										{category}
									</div>
									{categoryRoles.map((role) => (
										<div
											aria-selected={selectedRoles.includes(role.key)}
											className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
											key={role.key}
											onClick={() => handleToggle(role.key)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleToggle(role.key);
												}
											}}
											role="option"
											tabIndex={0}
										>
											<Checkbox
												checked={selectedRoles.includes(role.key)}
												className="pointer-events-none"
											/>
											<span>{role.label}</span>
										</div>
									))}
								</div>
							);
						})}
						{Object.keys(filteredRolesByCategory).length === 0 && (
							<div className="p-4 text-center text-muted-foreground text-sm">
								No roles found
							</div>
						)}
					</div>
				</ScrollArea>
			</PopoverContent>
		</Popover>
	);
}
